/**
 * NotificationRecipient — the role-keyed identity of the entity an
 * action targets. The runtime resolver (apps/worker/src/notify.ts)
 * walks the loan + contacts + LoanParty rows to turn a recipient role
 * into a concrete email address or phone number.
 *
 * The list lives here (and not in types.ts) because the DnD builder
 * uses `describeRecipient(role)` for tooltip copy and to flag which
 * roles still require manual contact setup on the loan record.
 *
 * Backwards compatibility: the union is a SUPERSET of the original
 * 'borrower' | 'mlo' | 'processor' | 'custom' enum, so every already-
 * stored Workflow row parses unchanged.
 */

// ---------------------------------------------------------------------------
// Role union — single source of truth
// ---------------------------------------------------------------------------

export const NOTIFICATION_RECIPIENTS = [
  'borrower', // primary borrower (LoanParty.role='borrower1')
  'borrower2', // co-borrower #2
  'borrower3', // co-borrower #3
  'borrower4', // co-borrower #4
  'non_borrowing_spouse', // manual entry (LoanParty.role='non_borrowing_spouse')
  'non_borrowing_individual', // manual entry (LoanParty.role='non_borrowing_individual')
  'mlo', // assigned MLO (loan.loanOfficerEmail / mloUser)
  'processor', // assigned processor (Contact role='processor')
  'loa', // Loan Officer Assistant (Contact role='assistant')
  'assigned', // whoever is currently assigned to this loan (loan.assignedToContact)
  'title_company', // settlement / title (loan.titleCompanyEmail)
  'custom', // free-form email/phone on the action
] as const;

export type NotificationRecipient = (typeof NOTIFICATION_RECIPIENTS)[number];

// ---------------------------------------------------------------------------
// Channel sub-unions
// ---------------------------------------------------------------------------

/**
 * Recipients valid for EMAIL — every role above has a mailbox.
 */
export const EMAIL_RECIPIENTS = [
  'borrower',
  'borrower2',
  'borrower3',
  'borrower4',
  'non_borrowing_spouse',
  'non_borrowing_individual',
  'mlo',
  'processor',
  'loa',
  'assigned',
  'title_company',
  'custom',
] as const;
export type EmailRecipient = (typeof EMAIL_RECIPIENTS)[number];

/**
 * Recipients valid for WHATSAPP / SMS. Title companies are mailbox-only
 * in Mana ops; processors are internal users notified via email + in-app.
 */
export const PHONE_RECIPIENTS = [
  'borrower',
  'borrower2',
  'borrower3',
  'borrower4',
  'non_borrowing_spouse',
  'non_borrowing_individual',
  'mlo',
  'loa',
  'assigned',
  'custom',
] as const;
export type PhoneRecipient = (typeof PHONE_RECIPIENTS)[number];

// ---------------------------------------------------------------------------
// Resolved recipient — what the action processor emits to the channel
// ---------------------------------------------------------------------------

export type Channel = 'email' | 'whatsapp' | 'sms';

export interface ResolvedRecipient {
  /** Human-friendly name shown in NotificationLog entries (e.g. "Pat Borrower"). */
  displayName: string;
  /** Concrete delivery channel for this resolved row. */
  channel: Channel;
  /**
   * The actual delivery target — email address for `channel === 'email'`,
   * E.164 phone number for `channel === 'whatsapp' | 'sms'`.
   */
  target: string;
}

// ---------------------------------------------------------------------------
// describeRecipient — palette tooltip + warning copy
// ---------------------------------------------------------------------------

export interface RecipientDescription {
  /** Short label shown on the recipient chip in the DnD builder. */
  label: string;
  /** One-sentence tooltip explaining who this routes to. */
  description: string;
  /**
   * When true, the recipient requires manual contact setup on the loan
   * record (Pipeline drawer) before the action can deliver. Drives the
   * "Set up contact" warning in the builder.
   */
  requiresContactSetup: boolean;
}

const RECIPIENT_DESCRIPTIONS: Readonly<Record<NotificationRecipient, RecipientDescription>> = {
  borrower: {
    label: 'Borrower 1',
    description: 'Primary borrower on the loan. Email and phone come from UWM Insights.',
    requiresContactSetup: false,
  },
  borrower2: {
    label: 'Borrower 2',
    description:
      'Co-borrower #2. UWM does not expose contact info for additional borrowers, so email and phone must be entered on the Pipeline drawer.',
    requiresContactSetup: true,
  },
  borrower3: {
    label: 'Borrower 3',
    description:
      'Co-borrower #3. UWM does not expose contact info for additional borrowers, so email and phone must be entered on the Pipeline drawer.',
    requiresContactSetup: true,
  },
  borrower4: {
    label: 'Borrower 4',
    description:
      'Co-borrower #4. UWM does not expose contact info for additional borrowers, so email and phone must be entered on the Pipeline drawer.',
    requiresContactSetup: true,
  },
  non_borrowing_spouse: {
    label: 'Non-borrowing spouse',
    description:
      'Spouse on title/community-property paperwork but not on the loan. Captured manually on the Pipeline drawer.',
    requiresContactSetup: true,
  },
  non_borrowing_individual: {
    label: 'Non-borrowing individual',
    description:
      'A non-spouse individual on title (e.g. trustee). Captured manually on the Pipeline drawer.',
    requiresContactSetup: true,
  },
  mlo: {
    label: 'MLO',
    description:
      "Assigned mortgage loan originator. Resolved from the loan's UWM-reported loan officer, falling back to the owning user.",
    requiresContactSetup: false,
  },
  processor: {
    label: 'Processor',
    description: "Assigned processor on the loan's contact list.",
    requiresContactSetup: true,
  },
  loa: {
    label: 'LOA',
    description: "Loan Officer Assistant on the loan's contact list.",
    requiresContactSetup: true,
  },
  assigned: {
    label: 'Assigned',
    description:
      "The contact currently assigned to this loan on the Pipeline drawer (whatever their role — processor, LOA, title, etc.). Skipped if no one is assigned.",
    requiresContactSetup: true,
  },
  title_company: {
    label: 'Title company',
    description: 'Settlement / title contact captured on the Pipeline drawer.',
    requiresContactSetup: true,
  },
  custom: {
    label: 'Custom',
    description:
      'Free-form email or phone number entered on the action itself. Use for one-off recipients.',
    requiresContactSetup: false,
  },
};

/**
 * Look up the human-friendly label + tooltip + setup-required flag for a
 * recipient role. Pure and total — every role in the union has an entry.
 */
export function describeRecipient(role: NotificationRecipient): RecipientDescription {
  return RECIPIENT_DESCRIPTIONS[role];
}

/**
 * True when this role is valid as the `to` value for an email action.
 */
export function isEmailRecipient(role: NotificationRecipient): role is EmailRecipient {
  return (EMAIL_RECIPIENTS as readonly string[]).includes(role);
}

/**
 * True when this role is valid as the `to` value for a WhatsApp or SMS action.
 */
export function isPhoneRecipient(role: NotificationRecipient): role is PhoneRecipient {
  return (PHONE_RECIPIENTS as readonly string[]).includes(role);
}
